import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { batchedIn } from '@/lib/batchedQuery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import BackgroundMedia from '@/components/BackgroundMedia';
import {
  Users2,
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ArrowRight,
  Link2,
  User,
  Heart,
  Baby,
  X,
  CalendarDays,
  BarChart3,
  ExternalLink,
  Search,
  Trash2,
} from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { familyStorage, FamilyMember, FamilyRelationship } from '@/lib/familyStorage';
import { scenarioStorage, LocalScenario } from '@/lib/scenarioStorage';
import { AddMemberForm } from '@/components/family/AddMemberForm';
import { MonthlyOverlapHeatmap } from '@/components/family/MonthlyOverlapHeatmap';
import { FamilyHospitalOptimizer } from '@/components/family/FamilyHospitalOptimizer';
import { CreateScenarioDialog } from '@/components/CreateScenarioDialog';
import { CreateCarePlanWizard } from '@/components/care-plan/CreateCarePlanWizard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

// ================================================================
// CONSTANTS
// ================================================================

const MEMBER_COLORS = [
  'var(--civic-navy)', // Navy
  'var(--civic-red)', // Red
  'var(--civic-gold)', // Gold
  '#5B8C5A', // Sage
  '#7B68AE', // Purple
  '#D4956A', // Copper
  'var(--civic-blue)', // Sky
  '#D94A7B', // Rose
];

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const RELATIONSHIP_ICONS: Record<FamilyRelationship, typeof User> = {
  self: User,
  spouse: Heart,
  child: Baby,
  parent: Users2,
  sibling: Users2,
  other: User,
};

// ================================================================
// HELPERS
// ================================================================

function parseDurationToDays(duration: string): number {
  const match = duration.match(/(\d+)(?:\s*[-–]\s*(\d+))?\s*(day|week|month|year)/i);
  if (!match) return 30;
  const num = parseInt(match[2] || match[1]);
  const unit = match[3].toLowerCase();
  if (unit.startsWith('day')) return num;
  if (unit.startsWith('week')) return num * 7;
  if (unit.startsWith('month')) return num * 30;
  if (unit.startsWith('year')) return num * 365;
  return 30;
}

function getMemberColor(members: FamilyMember[], memberId: string): string {
  const idx = members.findIndex((m) => m.id === memberId);
  return MEMBER_COLORS[idx >= 0 ? idx % MEMBER_COLORS.length : 0];
}

function fmtCost(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${n.toLocaleString()}`;
}

interface DbScenario {
  id: string;
  name: string;
  description: string | null;
  duration: string | null;
  total_procedures: number | null;
  estimated_cost: number | null;
  phases: string[] | null;
  tags: string[] | null;
  severity_level: number | null;
  slug: string | null;
  category: string | null;
  procedures: any[];
  real_total_cost?: number | null; // from scenario_costs_cache
}

interface TimelineScenario {
  scenario: LocalScenario;
  member: FamilyMember;
  startDate: Date;
  endDate: Date;
  color: string;
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export default function FamilyPlanner() {
  const { t } = useTranslation('family');
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [selectedMemberFilter, setSelectedMemberFilter] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCarePlanWizard, setShowCarePlanWizard] = useState(false);
  const [carePlanMemberName, setCarePlanMemberName] = useState<string | undefined>();
  const [expandedBarId, setExpandedBarId] = useState<string | null>(null);

  // Timeline navigation — show 12 months starting from current month
  const now = new Date();
  const [timelineYear, setTimelineYear] = useState(now.getFullYear());
  const [timelineStartMonth, setTimelineStartMonth] = useState(now.getMonth());

  // Data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const members = useMemo(() => familyStorage.getMembers(), [refreshKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allScenarios = useMemo(() => scenarioStorage.getUserScenarios(), [refreshKey]);

  // Member → scenarios map
  const { memberScenarios, unassigned } = useMemo(() => {
    const map: Record<string, LocalScenario[]> = {};
    members.forEach((m) => { map[m.id] = []; });
    const unassignedArr: LocalScenario[] = [];

    allScenarios.forEach((scenario) => {
      if (scenario.familyMemberId && map[scenario.familyMemberId]) {
        map[scenario.familyMemberId].push(scenario);
      } else {
        unassignedArr.push(scenario);
      }
    });
    return { memberScenarios: map, unassigned: unassignedArr };
  }, [members, allScenarios]);

  const grandTotal = allScenarios.reduce((sum, s) => sum + (s.estimatedCost || 0), 0);

  const memberTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    members.forEach((m) => {
      totals[m.id] = (memberScenarios[m.id] || []).reduce(
        (sum, s) => sum + (s.estimatedCost || 0),
        0
      );
    });
    return totals;
  }, [members, memberScenarios]);

  // ---- 12-month window ----
  const timelineMonths = useMemo(() => {
    const months: { date: Date; label: string; key: string }[] = [];
    for (let i = 0; i < 12; i++) {
      let m = timelineStartMonth + i;
      let y = timelineYear;
      while (m >= 12) { m -= 12; y += 1; }
      const date = new Date(y, m, 1);
      const suffix = y !== timelineYear ? ` '${String(y).slice(2)}` : '';
      months.push({
        date,
        label: `${MONTH_LABELS[m]}${suffix}`,
        key: `${y}-${String(m + 1).padStart(2, '0')}`,
      });
    }
    return months;
  }, [timelineYear, timelineStartMonth]);

  // ---- Timeline scenarios (only those with plannedDate) ----
  const timelineScenarios = useMemo(() => {
    const result: TimelineScenario[] = [];
    for (const member of members) {
      for (const scenario of memberScenarios[member.id] || []) {
        if (!scenario.plannedDate) continue;
        const startDate = new Date(scenario.plannedDate);
        const durationDays = parseDurationToDays(scenario.duration || '1 month');
        const endDate = new Date(startDate.getTime() + durationDays * 86400000);
        result.push({
          scenario,
          member,
          startDate,
          endDate,
          color: getMemberColor(members, member.id),
        });
      }
    }
    // Sort by start date
    result.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    return result;
  }, [members, memberScenarios]);

  // ---- Monthly cost data for Recharts ----
  const monthlyCostData = useMemo(() => {
    const buckets: Record<string, Record<string, number> & { month: string }> = {};

    for (const mInfo of timelineMonths) {
      const obj: any = { month: mInfo.label };
      members.forEach((mem) => {
        obj[familyStorage.getDisplayName(mem)] = 0;
      });
      buckets[mInfo.key] = obj;
    }

    for (const ts of timelineScenarios) {
      const memberName = familyStorage.getDisplayName(ts.member);
      const durationDays = Math.max(1, parseDurationToDays(ts.scenario.duration || '1 month'));
      const costPerDay = (ts.scenario.estimatedCost || 0) / durationDays;

      for (const mInfo of timelineMonths) {
        const [y, m] = mInfo.key.split('-').map(Number);
        const monthStart = new Date(y, m - 1, 1);
        const monthEnd = new Date(y, m, 0); // last day

        const overlapStart = Math.max(ts.startDate.getTime(), monthStart.getTime());
        const overlapEnd = Math.min(ts.endDate.getTime(), monthEnd.getTime());

        if (overlapStart <= overlapEnd) {
          const overlapDays = Math.ceil((overlapEnd - overlapStart) / 86400000) + 1;
          const bucket = buckets[mInfo.key];
          if (bucket) {
            (bucket as any)[memberName] =
              Math.round(((bucket as any)[memberName] || 0) + costPerDay * overlapDays);
          }
        }
      }
    }

    return Object.values(buckets);
  }, [timelineScenarios, timelineMonths, members]);

  const hasChartData = monthlyCostData.some((row) =>
    Object.entries(row).some(([k, v]) => k !== 'month' && typeof v === 'number' && v > 0)
  );

  // ---- Handlers ----
  const handleAssignScenario = (dbScenario: DbScenario, memberId: string, plannedDate: string) => {
    const realCost = Number(dbScenario.real_total_cost || dbScenario.estimated_cost) || 0;
    const procCount = dbScenario.procedures?.length || dbScenario.total_procedures || 0;
    const procsList = (dbScenario.procedures || []).map((p: any) => ({
      code: p.code,
      codeType: p.code_type,
      description: p.description || '',
      phase: p.phase || '',
      quantity: p.quantity || 1,
      estimatedCost: Number(p.estimated_cost) || 0,
      dayNumber: p.day_number || undefined,
      setting: p.setting || undefined,
      frequency: p.frequency || undefined,
    }));

    // Check if this DB scenario is already saved locally (match by name)
    const existing = allScenarios.find((s) => s.name === dbScenario.name);

    if (existing) {
      // Already local — update assignment, date, AND refresh data from DB
      scenarioStorage.assignToFamilyMember(existing.id, memberId);
      scenarioStorage.updateScenario(existing.id, {
        plannedDate: plannedDate || undefined,
        totalProcedures: procCount,
        estimatedCost: realCost,
        procedures: procsList,
        duration: dbScenario.duration || existing.duration,
        description: dbScenario.description || existing.description,
        phases: Array.isArray(dbScenario.phases) ? dbScenario.phases : existing.phases,
      });
    } else {
      // Save DB scenario as local copy with assignment
      scenarioStorage.saveScenario({
        name: dbScenario.name,
        description: dbScenario.description || '',
        duration: dbScenario.duration || '1 month',
        totalProcedures: procCount,
        estimatedCost: realCost,
        procedures: procsList,
        phases: Array.isArray(dbScenario.phases) ? dbScenario.phases : [],
        tags: dbScenario.tags || [],
        severityLevel: dbScenario.severity_level || undefined,
        isDraft: false,
        visibility: 'unlisted',
        shareToken: '',
        source: 'template',
        familyMemberId: memberId,
        plannedDate: plannedDate || undefined,
      });
    }

    refresh();
    setShowAssignPanel(false);
  };

  const handleUnassign = (scenarioId: string) => {
    scenarioStorage.assignToFamilyMember(scenarioId, null);
    scenarioStorage.updateScenario(scenarioId, { plannedDate: undefined });
    refresh();
  };

  const handleDateChange = (scenarioId: string, date: string) => {
    scenarioStorage.updateScenario(scenarioId, { plannedDate: date || undefined });
    refresh();
  };

  const handleDeleteScenario = (scenarioId: string, name: string) => {
    if (!confirm(`Remove "${name}" from your family plan?`)) return;
    scenarioStorage.deleteScenario(scenarioId);
    refresh();
  };

  const navigateTimeline = (dir: number) => {
    let newMonth = timelineStartMonth + dir * 3;
    let newYear = timelineYear;
    while (newMonth < 0) { newMonth += 12; newYear -= 1; }
    while (newMonth >= 12) { newMonth -= 12; newYear += 1; }
    setTimelineStartMonth(newMonth);
    setTimelineYear(newYear);
  };

  // ---- Timeline bar positioning ----
  function getBarPosition(ts: TimelineScenario) {
    const timelineStart = timelineMonths[0].date.getTime();
    const lastMonth = timelineMonths[11].date;
    const timelineEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getTime();
    const totalMs = timelineEnd - timelineStart;
    if (totalMs <= 0) return { left: '0%', width: '2%' };

    const barStart = Math.max(0, ((ts.startDate.getTime() - timelineStart) / totalMs) * 100);
    const barEnd = Math.min(100, ((ts.endDate.getTime() - timelineStart) / totalMs) * 100);
    const width = Math.max(2, barEnd - barStart);

    return { left: `${barStart}%`, width: `${width}%` };
  }

  // Filtered view
  const visibleScenarios = selectedMemberFilter
    ? allScenarios.filter((s) => s.familyMemberId === selectedMemberFilter)
    : allScenarios;

  const visibleTimeline = selectedMemberFilter
    ? timelineScenarios.filter((ts) => ts.member.id === selectedMemberFilter)
    : timelineScenarios;

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <>
      <Helmet>
        <title>{t('meta.pageTitle', { brandName: brand.name })}</title>
      </Helmet>
      {brand.key === 'luigis' && <BackgroundMedia />}

      <div className={`min-h-screen ${isAhp ? (isDark ? 'bg-civic-bg-dark' : 'bg-civic-cream') : ''} pb-24`}>
        <div className="max-w-6xl mx-auto px-4 py-8">

          {/* ===== HEADER ===== */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isAhp ? 'bg-civic-navy/10' : 'bg-matrix-green/10'
              }`}>
                <Users2 className={`w-5 h-5 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${
                  isAhp ? (isDark ? 'text-civic-cream font-serif' : 'text-gray-900 font-serif') : 'font-mono text-matrix-green'
                }`}>
                  {t('pageTitle')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('pageSubtitle')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => setShowCreateDialog(true)}
                size="sm"
                className={`whitespace-nowrap ${isAhp
                  ? 'bg-civic-navy hover:bg-[var(--civic-navy-deep)] text-white'
                  : 'bg-matrix-green text-black hover:bg-matrix-green/90'}`}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('plan.createCustom')}
              </Button>
              <Button
                onClick={() => setShowAssignPanel(!showAssignPanel)}
                size="sm"
                variant="outline"
                className={isAhp ? 'border-civic-navy/20 text-civic-navy hover:bg-civic-navy/5' : ''}
              >
                <Link2 className="w-4 h-4 mr-1" />
                {t('plan.assignScenario')}
              </Button>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                size="sm"
                variant="outline"
                className={isAhp ? 'border-gray-300' : ''}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('addMember.button')}
              </Button>
            </div>
          </div>

          {/* Add member form */}
          {showAddForm && (
            <div className={`mb-6 p-4 rounded-xl border ${
              isAhp ? (isDark ? 'border-civic-cream/10 bg-civic-navy-panel' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
            }`}>
              <AddMemberForm
                onAdded={() => { setShowAddForm(false); refresh(); }}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          )}

          {/* ===== MEMBER PILLS ===== */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
            {/* All pill */}
            <button
              onClick={() => setSelectedMemberFilter(null)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                selectedMemberFilter === null
                  ? (isAhp
                    ? 'bg-civic-navy text-white border-civic-navy shadow-sm'
                    : 'bg-matrix-green/20 text-matrix-green border-matrix-green')
                  : (isAhp
                    ? (isDark ? 'bg-civic-navy-panel text-civic-cream/70 border-civic-cream/10 hover:border-civic-cream/20' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300')
                    : 'bg-card text-foreground/70 border-foreground/10')
              }`}
            >
              <Users2 className="w-3.5 h-3.5" />
              <span>All</span>
              <span className="font-mono font-bold">{fmtCost(grandTotal)}</span>
            </button>

            {members.map((member, idx) => {
              const Icon = RELATIONSHIP_ICONS[member.relationship];
              const color = MEMBER_COLORS[idx % MEMBER_COLORS.length];
              const isSelected = selectedMemberFilter === member.id;
              const total = memberTotals[member.id] || 0;
              const count = (memberScenarios[member.id] || []).length;

              return (
                <button
                  key={member.id}
                  onClick={() => setSelectedMemberFilter(isSelected ? null : member.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                    isSelected
                      ? 'text-white shadow-sm'
                      : (isAhp
                        ? 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        : 'bg-card text-foreground/70 border-foreground/10')
                  }`}
                  style={isSelected ? { backgroundColor: color, borderColor: color } : {}}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : `${color}15`,
                    }}
                  >
                    <Icon
                      className="w-3 h-3"
                      style={{ color: isSelected ? 'white' : color }}
                    />
                  </div>
                  <span>{familyStorage.getDisplayName(member)}</span>
                  {count > 0 && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {count}
                    </Badge>
                  )}
                  {total > 0 && (
                    <span className="font-mono font-bold">{fmtCost(total)}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ===== ASSIGN SCENARIO PANEL ===== */}
          {showAssignPanel && (
            <AssignPanel
              isAhp={isAhp}
              isDark={isDark}
              members={members}
              localScenarios={allScenarios}
              onAssign={handleAssignScenario}
              onClose={() => setShowAssignPanel(false)}
              defaultMemberId={selectedMemberFilter}
            />
          )}

          {/* ===== GRAND TOTAL BANNER ===== */}
          <div className={`rounded-xl p-5 mb-6 ${
            isAhp
              ? 'bg-gradient-to-r from-civic-navy to-[var(--civic-navy)] text-white'
              : 'bg-gradient-to-r from-matrix-green/20 to-matrix-green/5 border border-matrix-green/20'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider mb-1 ${
                  isAhp ? 'text-white/70' : 'text-muted-foreground'
                }`}>
                  {t('results.estimatedAnnualCost')}
                </p>
                <p className={`text-3xl font-bold font-mono ${
                  isAhp ? 'text-white' : 'text-matrix-green'
                }`}>
                  ${grandTotal.toLocaleString()}
                </p>
              </div>
              <div className="text-right space-y-2">
                <div className={`text-xs ${isAhp ? 'text-white/70' : 'text-muted-foreground'}`}>
                  {members.length} member{members.length !== 1 ? 's' : ''} &middot;{' '}
                  {allScenarios.length} scenario{allScenarios.length !== 1 ? 's' : ''}
                </div>
                {allScenarios.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => { setCarePlanMemberName(undefined); setShowCarePlanWizard(true); }}
                    className={isAhp
                      ? 'bg-white text-civic-navy hover:bg-white/90 text-xs'
                      : 'bg-black text-matrix-green hover:bg-black/80 text-xs'
                    }
                  >
                    <ArrowRight className="w-3.5 h-3.5 mr-1" />
                    {t('results.findBestHospital')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* ===== TIMELINE ===== */}
          <div className={`rounded-xl border p-5 mb-6 ${
            isAhp ? (isDark ? 'border-civic-cream/10 bg-civic-navy-panel' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className={`w-4 h-4 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
                <h2 className={`text-base font-semibold ${
                  isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'
                }`}>
                  {t('plan.timeline')}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => navigateTimeline(-1)} className="h-7 w-7 p-0">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs font-medium px-2 min-w-[80px] text-center">
                  {MONTH_LABELS[timelineStartMonth]} {timelineYear}
                </span>
                <Button variant="ghost" size="sm" onClick={() => navigateTimeline(1)} className="h-7 w-7 p-0">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Month column headers */}
            <div className="relative">
              <div className="flex mb-2">
                {timelineMonths.map((m, i) => (
                  <div
                    key={i}
                    className={`flex-1 text-center text-[10px] font-medium pb-2 border-b ${
                      isAhp ? (isDark ? 'text-civic-cream/50 border-civic-cream/8' : 'text-gray-500 border-gray-200') : 'text-muted-foreground border-foreground/10'
                    }`}
                  >
                    {m.label}
                  </div>
                ))}
              </div>

              {/* Month grid lines + Today indicator */}
              <div className="absolute top-8 left-0 right-0 bottom-0 flex pointer-events-none" aria-hidden>
                {timelineMonths.map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 border-r ${
                      isAhp ? (isDark ? 'border-civic-cream/4' : 'border-gray-100') : 'border-foreground/5'
                    } ${i === 0 ? 'border-l' : ''}`}
                  />
                ))}
                {/* Today line */}
                {(() => {
                  const timelineStart = timelineMonths[0].date.getTime();
                  const lastMonth = timelineMonths[11].date;
                  const timelineEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getTime();
                  const totalMs = timelineEnd - timelineStart;
                  const nowMs = now.getTime();
                  if (nowMs >= timelineStart && nowMs <= timelineEnd && totalMs > 0) {
                    const pct = ((nowMs - timelineStart) / totalMs) * 100;
                    return (
                      <div
                        className="absolute top-0 bottom-0 w-px border-l-2 border-dashed z-10"
                        style={{
                          left: `${pct}%`,
                          borderColor: isAhp ? 'var(--civic-red)' : '#4ade80',
                        }}
                      >
                        <div className={`absolute -top-1 left-1/2 -translate-x-1/2 text-[8px] font-bold px-1 rounded ${
                          isAhp ? 'bg-civic-red text-white' : 'bg-matrix-green text-black'
                        }`}>
                          TODAY
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Scenario bars */}
              {visibleTimeline.length > 0 ? (
                <div className="space-y-1.5 pt-2 relative min-h-[60px]">
                  {visibleTimeline.map((ts) => {
                    const pos = getBarPosition(ts);
                    const isExpanded = expandedBarId === ts.scenario.id;
                    // Check for overlaps with same member
                    const overlapping = visibleTimeline.filter(
                      other => other.scenario.id !== ts.scenario.id &&
                        other.member.id === ts.member.id &&
                        other.startDate < ts.endDate &&
                        other.endDate > ts.startDate
                    );
                    return (
                      <div key={ts.scenario.id}>
                        <div className="relative h-9">
                          <div
                            className="absolute top-0 h-full rounded-lg flex items-center px-2.5 cursor-pointer hover:brightness-110 transition-all overflow-hidden shadow-sm"
                            style={{
                              left: pos.left,
                              width: pos.width,
                              backgroundColor: ts.color,
                              minWidth: '80px',
                            }}
                            title={`${ts.scenario.name} — ${familyStorage.getDisplayName(ts.member)} — $${ts.scenario.estimatedCost?.toLocaleString()} — ${ts.scenario.duration}`}
                            onClick={() => setExpandedBarId(isExpanded ? null : ts.scenario.id)}
                          >
                            <span className="text-[11px] text-white font-medium truncate">
                              {ts.scenario.name}
                            </span>
                            {overlapping.length > 0 && (
                              <span className="ml-1 w-4 h-4 rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center flex-shrink-0" title="Overlaps with another scenario">
                                !
                              </span>
                            )}
                            <span className="text-[10px] text-white/80 ml-auto pl-2 flex-shrink-0 font-mono">
                              {fmtCost(ts.scenario.estimatedCost || 0)}
                            </span>
                          </div>
                        </div>
                        {/* Expanded mini-detail */}
                        {isExpanded && (
                          <div className={`ml-4 mt-1 mb-2 p-2.5 rounded-lg border text-[11px] ${
                            isAhp ? (isDark ? 'border-civic-cream/8 bg-[var(--civic-navy-deep)]' : 'border-gray-200 bg-gray-50') : 'border-foreground/10 bg-muted/30'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{ts.scenario.name}</span>
                              <button
                                onClick={() => navigate(`/scenarios/${ts.scenario.uuid}/${ts.scenario.slug}`)}
                                className={`text-[10px] underline ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`}
                              >
                                {t('plan.viewDetails', 'View Details')}
                              </button>
                            </div>
                            <div className="flex items-center gap-3 text-muted-foreground">
                              <span>{ts.scenario.phases?.length || 0} phases</span>
                              <span>{ts.scenario.totalProcedures || ts.scenario.procedures?.length || 0} procedures</span>
                              <span>{ts.scenario.duration}</span>
                              <span className="font-mono font-bold">${(ts.scenario.estimatedCost || 0).toLocaleString()}</span>
                            </div>
                            {ts.scenario.preferredHospitalName && (
                              <div className="mt-1 text-muted-foreground">
                                Hospital: {ts.scenario.preferredHospitalName}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 relative">
                  <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p className={`text-sm font-medium mb-1 ${isAhp ? (isDark ? 'text-civic-cream/60' : 'text-gray-600') : 'text-foreground/60'}`}>
                    {t('plan.noScenarios')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('plan.assignHint', 'Assign scenarios to family members with planned dates to see them on the timeline')}
                  </p>
                </div>
              )}

              {/* Legend */}
              {visibleTimeline.length > 0 && !selectedMemberFilter && (
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 dark:border-foreground/10">
                  {members.map((member, idx) => {
                    const count = timelineScenarios.filter((ts) => ts.member.id === member.id).length;
                    if (count === 0) return null;
                    return (
                      <div key={member.id} className="flex items-center gap-1.5 text-[11px]">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: MEMBER_COLORS[idx % MEMBER_COLORS.length] }}
                        />
                        <span className="text-muted-foreground">
                          {familyStorage.getDisplayName(member)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ===== HEATMAP ===== */}
          <MonthlyOverlapHeatmap
            members={members}
            memberScenarios={memberScenarios}
            timelineMonths={timelineMonths}
          />

          {/* ===== HOSPITAL OPTIMIZER ===== */}
          <div className="mb-6">
            <FamilyHospitalOptimizer
              scenarios={allScenarios}
              members={members}
              onRefresh={refresh}
            />
          </div>

          {/* ===== MONTHLY COST CHART ===== */}
          {hasChartData && (
            <div className={`rounded-xl border p-5 mb-6 ${
              isAhp ? (isDark ? 'border-civic-cream/10 bg-civic-navy-panel' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
            }`}>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className={`w-4 h-4 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
                <h2 className={`text-base font-semibold ${isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'}`}>
                  {t('results.costByMonth')}
                </h2>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyCostData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isAhp ? (isDark ? 'rgba(var(--civic-cream-rgb),0.06)' : '#e5e7eb') : 'rgba(255,255,255,0.05)'}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: isAhp ? (isDark ? 'rgba(var(--civic-cream-rgb),0.5)' : '#6b7280') : '#999' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: isAhp ? (isDark ? 'rgba(var(--civic-cream-rgb),0.5)' : '#6b7280') : '#999' }}
                    tickFormatter={(v: number) =>
                      v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isAhp ? (isDark ? 'var(--civic-navy-panel)' : '#fff') : '#1a1a1a',
                      border: `1px solid ${isAhp ? (isDark ? 'rgba(var(--civic-cream-rgb),0.1)' : '#e5e7eb') : '#333'}`,
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: isDark ? 'var(--civic-cream)' : undefined,
                    }}
                    formatter={(value: number, name: string) => [
                      `$${value.toLocaleString()}`,
                      name,
                    ]}
                  />
                  {members.map((member, idx) => (
                    <Bar
                      key={member.id}
                      dataKey={familyStorage.getDisplayName(member)}
                      stackId="cost"
                      fill={MEMBER_COLORS[idx % MEMBER_COLORS.length]}
                      radius={
                        idx === members.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]
                      }
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>

              {/* Chart legend */}
              <div className="flex items-center justify-center gap-4 mt-2">
                {members.map((member, idx) => {
                  const total = memberTotals[member.id] || 0;
                  if (total === 0) return null;
                  return (
                    <div key={member.id} className="flex items-center gap-1.5 text-[11px]">
                      <div
                        className="w-2.5 h-2.5 rounded-sm"
                        style={{ backgroundColor: MEMBER_COLORS[idx % MEMBER_COLORS.length] }}
                      />
                      <span className="text-muted-foreground">
                        {familyStorage.getDisplayName(member)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== SCENARIO LIST ===== */}
          <div className={`rounded-xl border p-5 ${
            isAhp ? (isDark ? 'border-civic-cream/10 bg-civic-navy-panel' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-base font-semibold flex items-center gap-2 ${
                isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'
              }`}>
                <DollarSign className={`w-4 h-4 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
                {t('results.totalScenarios', 'Scenarios')}
              </h2>
              <Link to="/scenarios">
                <Button
                  size="sm"
                  variant="outline"
                  className={isAhp ? 'border-civic-red/20 text-civic-red hover:bg-civic-red/5' : ''}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {t('plan.createCustom')}
                </Button>
              </Link>
            </div>

            {visibleScenarios.length > 0 ? (
              <div className="space-y-2">
                {visibleScenarios.map((scenario) => {
                  const member = members.find((m) => m.id === scenario.familyMemberId);
                  const memberIdx = member ? members.indexOf(member) : -1;
                  const color = member
                    ? MEMBER_COLORS[memberIdx % MEMBER_COLORS.length]
                    : '#d1d5db';

                  return (
                    <div
                      key={scenario.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        isAhp
                          ? (isDark ? 'border-civic-cream/5 bg-civic-cream/3 hover:bg-civic-cream/6' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-50')
                          : 'border-foreground/5 bg-muted/30 hover:bg-muted/50'
                      }`}
                    >
                      {/* Color bar */}
                      <div
                        className="w-1.5 h-10 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />

                      {/* Scenario info */}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/scenarios/${scenario.uuid}/${scenario.slug}`}
                          className={`text-sm font-medium hover:underline truncate block ${
                            isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'
                          }`}
                        >
                          {scenario.name}
                          <ExternalLink className="w-3 h-3 inline ml-1 opacity-40" />
                        </Link>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                          <span>{scenario.totalProcedures} {t('results.totalProcedures', 'procedures').toLowerCase()}</span>
                          <span>&middot;</span>
                          <span>{scenario.duration}</span>
                        </div>
                      </div>

                      {/* Member picker */}
                      <Select
                        value={scenario.familyMemberId || '__none'}
                        onValueChange={(v) => {
                          if (v === '__none') {
                            handleUnassign(scenario.id);
                          } else {
                            scenarioStorage.assignToFamilyMember(scenario.id, v);
                            refresh();
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 w-28 text-[11px]">
                          <SelectValue placeholder="Assign..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none" className="text-xs">
                            {t('results.unassigned')}
                          </SelectItem>
                          {members.map((m) => (
                            <SelectItem key={m.id} value={m.id} className="text-xs">
                              {familyStorage.getDisplayName(m)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Date picker */}
                      <div className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <Input
                          type="date"
                          value={scenario.plannedDate?.split('T')[0] || ''}
                          onChange={(e) => handleDateChange(scenario.id, e.target.value)}
                          className="h-8 w-[130px] text-[11px]"
                        />
                      </div>

                      {/* Cost */}
                      <span className={`text-sm font-mono font-bold flex-shrink-0 ${
                        isAhp ? 'text-civic-red' : 'text-matrix-green'
                      }`}>
                        ${(scenario.estimatedCost || 0).toLocaleString()}
                      </span>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteScenario(scenario.id, scenario.name)}
                        className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                        title={t('plan.removeScenario')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10">
                <DollarSign className={`w-10 h-10 mx-auto mb-2 ${
                  isAhp ? (isDark ? 'text-civic-cream/20' : 'text-gray-300') : 'text-foreground/20'
                }`} />
                <h3 className={`text-sm font-semibold mb-1 ${
                  isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'
                }`}>
                  {selectedMemberFilter ? t('plan.noScenarios') : t('empty.noMembers')}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('empty.getStarted')}
                </p>
                <Link to="/scenarios">
                  <Button
                    size="sm"
                    className={isAhp
                      ? 'bg-civic-red hover:bg-[#9a1d2d] text-white'
                      : 'bg-matrix-green text-black hover:bg-matrix-green/90'
                    }
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('plan.createCustom')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Scenario Dialog (full-screen overlay) */}
      <CreateScenarioDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onScenarioCreated={() => {
          setShowCreateDialog(false);
          refresh();
        }}
        initialFamilyMemberId={selectedMemberFilter}
      />

      {/* Care Plan Wizard */}
      {showCarePlanWizard && (
        <CreateCarePlanWizard
          onClose={() => setShowCarePlanWizard(false)}
          familyMemberName={carePlanMemberName}
        />
      )}
    </>
  );
}

// ================================================================
// ASSIGN PANEL — fetches scenarios from Supabase DB
// ================================================================

function AssignPanel({
  isAhp,
  isDark,
  members,
  localScenarios,
  onAssign,
  onClose,
  defaultMemberId,
}: {
  isAhp: boolean;
  isDark: boolean;
  members: FamilyMember[];
  localScenarios: LocalScenario[];
  onAssign: (scenario: DbScenario, memberId: string, date: string) => void;
  onClose: () => void;
  defaultMemberId: string | null;
}) {
  const { t } = useTranslation('family');
  const [search, setSearch] = useState('');
  const [selectedDbScenario, setSelectedDbScenario] = useState<DbScenario | null>(null);
  const [selectedMember, setSelectedMember] = useState(
    (defaultMemberId && members.some(m => m.id === defaultMemberId) ? defaultMemberId : members[0]?.id) || ''
  );
  const [plannedDate, setPlannedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Sync selectedMember when the member filter tab changes
  useEffect(() => {
    if (defaultMemberId && members.some(m => m.id === defaultMemberId)) {
      setSelectedMember(defaultMemberId);
    }
  }, [defaultMemberId, members]);

  // Fetch scenarios + procedures + real costs from Supabase
  const { data: dbScenarios = [], isLoading } = useQuery({
    queryKey: ['family-planner-scenarios'],
    queryFn: async () => {
      const { data: scenariosData, error } = await supabase
        .from('scenarios')
        .select('id, name, description, duration, total_procedures, estimated_cost, phases, tags, severity_level, slug, category')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;

      // Fetch procedures for all scenarios (batched to avoid URL length limits)
      const ids = (scenariosData || []).map((s) => s.id);
      const [{ data: procsData }, { data: costsData }] = await Promise.all([
        batchedIn(
          'scenario_procedures',
          'scenario_id',
          ids,
          'scenario_id, code, code_type, description, phase, quantity, estimated_cost, day_number, setting, frequency',
          { column: 'sort_order', ascending: true }
        ),
        batchedIn(
          'scenario_costs_cache',
          'scenario_id',
          ids,
          'scenario_id, real_total_cost, total_procedures'
        ),
      ]);

      const procsByScenario: Record<string, any[]> = {};
      (procsData || []).forEach((p) => {
        if (!procsByScenario[p.scenario_id]) procsByScenario[p.scenario_id] = [];
        procsByScenario[p.scenario_id].push(p);
      });

      const costsByScenario: Record<string, { real_total_cost: number; total_procedures: number }> = {};
      (costsData || []).forEach((c) => {
        costsByScenario[c.scenario_id] = {
          real_total_cost: Number(c.real_total_cost) || 0,
          total_procedures: Number(c.total_procedures) || 0,
        };
      });

      return (scenariosData || []).map((s) => {
        const procs = procsByScenario[s.id] || [];
        const cache = costsByScenario[s.id];
        return {
          ...s,
          procedures: procs,
          // Use real pricing from cache, fallback to estimated_cost, then sum of procedure estimates
          real_total_cost: cache?.real_total_cost || null,
          total_procedures: procs.length || cache?.total_procedures || s.total_procedures || 0,
          estimated_cost: cache?.real_total_cost || s.estimated_cost || 0,
        };
      }) as DbScenario[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Already-assigned names for dimming
  const assignedNames = new Set(localScenarios.map((s) => s.name));

  // Filter by search
  const filtered = search.trim()
    ? dbScenarios.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        (s.category || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.tags || []).some((t) => t.toLowerCase().includes(search.toLowerCase()))
      )
    : dbScenarios;

  return (
    <div className={`mb-6 rounded-xl border overflow-hidden ${
      isAhp ? (isDark ? 'border-[rgba(91,155,213,0.15)] bg-civic-navy-panel' : 'border-civic-navy/15 bg-white') : 'border-matrix-green/20 bg-card'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-5 py-3 ${
        isAhp ? 'bg-civic-navy/[0.04] border-b border-civic-navy/10' : 'bg-matrix-green/5 border-b border-matrix-green/10'
      }`}>
        <h3 className={`text-sm font-semibold ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`}>
          {t('plan.assignScenario')}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-3 h-3" />
        </Button>
      </div>

      <div className="p-5">
        {/* Search + controls row */}
        <div className="flex items-end gap-3 mb-4 flex-wrap">
          <div className="flex-1 min-w-[200px] space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              {t('plan.searchScenarios', 'Search Scenarios')}
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder={t('plan.searchPlaceholder', 'Search by name, category, or tag...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 text-sm"
                autoFocus
              />
            </div>
          </div>

          <div className="w-36 space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              {t('plan.familyMember', 'Family Member')}
            </label>
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {familyStorage.getDisplayName(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-40 space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              {t('plan.startDate')}
            </label>
            <Input
              type="date"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
              className="h-9"
            />
          </div>
        </div>

        {/* Scenario list */}
        <div className={`rounded-lg border max-h-[300px] overflow-y-auto ${
          isAhp ? (isDark ? 'border-civic-cream/10' : 'border-gray-200') : 'border-foreground/10'
        }`}>
          {isLoading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              {t('plan.loadingScenarios', 'Loading scenarios...')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              {search ? t('plan.noSearchResults', 'No scenarios match your search') : t('plan.noScenarios')}
            </div>
          ) : (
            filtered.map((scenario) => {
              const isAlreadyAssigned = assignedNames.has(scenario.name);
              const isSelected = selectedDbScenario?.id === scenario.id;

              return (
                <button
                  key={scenario.id}
                  type="button"
                  onClick={() => setSelectedDbScenario(isSelected ? null : scenario)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b last:border-b-0 transition-colors ${
                    isSelected
                      ? (isAhp ? (isDark ? 'bg-[rgba(91,155,213,0.08)]' : 'bg-civic-navy/[0.06]') : 'bg-matrix-green/10')
                      : (isAhp ? (isDark ? 'hover:bg-civic-cream/4' : 'hover:bg-gray-50') : 'hover:bg-muted/50')
                  } ${isAhp ? (isDark ? 'border-civic-cream/4' : 'border-gray-100') : 'border-foreground/5'}`}
                >
                  {/* Selection indicator */}
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    isSelected
                      ? (isAhp ? (isDark ? 'border-[#5B9BD5] bg-[#5B9BD5]' : 'border-civic-navy bg-civic-navy') : 'border-matrix-green bg-matrix-green')
                      : (isAhp ? (isDark ? 'border-civic-cream/20' : 'border-gray-300') : 'border-foreground/20')
                  }`}>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${
                        isAhp ? (isDark ? 'text-civic-cream' : 'text-gray-900') : 'text-foreground'
                      }`}>
                        {scenario.name}
                      </span>
                      {isAlreadyAssigned && (
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">
                          {t('plan.added', 'Added')}
                        </Badge>
                      )}
                      {scenario.category && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">
                          {scenario.category}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {scenario.procedures?.length || scenario.total_procedures || 0} {t('results.totalProcedures', 'procedures').toLowerCase()} &middot; {scenario.duration || '—'}
                    </div>
                  </div>

                  {/* Cost */}
                  <span className={`text-sm font-mono font-bold flex-shrink-0 ${
                    isAhp ? 'text-civic-red' : 'text-matrix-green'
                  }`}>
                    {fmtCost(Number(scenario.real_total_cost || scenario.estimated_cost) || 0)}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Assign button */}
        <div className="flex justify-end mt-4">
          <Button
            onClick={() => {
              if (selectedDbScenario && selectedMember) {
                onAssign(selectedDbScenario, selectedMember, plannedDate);
              }
            }}
            size="sm"
            disabled={!selectedDbScenario || !selectedMember}
            className={`h-9 ${
              isAhp
                ? 'bg-civic-red hover:bg-[#9a1d2d] text-white'
                : 'bg-matrix-green text-black hover:bg-matrix-green/90'
            }`}
          >
            <ArrowRight className="w-4 h-4 mr-1" />
            {t('plan.assignScenario')} {selectedDbScenario ? `"${selectedDbScenario.name}"` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
